import { useEffect, useState } from "react";
import { AttributeFieldProps } from "./RenderAttributeInput";
import { GetBusinessMemberships, GetBusinessPackages, GetBusinessStaff } from "vagaro-website-rendering/src/components/Utilities/BusinessData";
import { SearchIcon } from "../SelectDropdown";
import { getNodeComponentAttributeValue } from "vagaro-website-rendering/src/components/Definitions/LayoutNode";
import { AttributeTargetType } from "../Data/WBData";
import { CheckItem } from "./CategorySelector";
import { DownCaret } from "../Icons/Carets";
import { componentAttributeTypes } from "vagaro-website-rendering/src/components/Definitions/AttributeTypes";

type CheckboxData = {
    name: string;
    checked: boolean;
    hashItemId: string;
};

enum FilterDataSelectStatus {
    None = 0,
    All = 1,
    Some = 2,
};

type FilterData = {
    status: FilterDataSelectStatus; 
    excludedIds: string[];
    includedIds: string[];
};

type FilterConfiguration = {
    labelPlural:string,
    labelSingle:string
};

const MembershipFilterField = ({ 
    siteData, 
    layoutObject, 
    definition, 
    updateFunc, 
}: AttributeFieldProps) => {
    const defaultFilterData: FilterData = {
        status: FilterDataSelectStatus.All,
        includedIds: [],
        excludedIds: [],
    };
    const filterString: string = getNodeComponentAttributeValue(layoutObject,definition.attKey,JSON.stringify(defaultFilterData));
    const parsedFilter: FilterData = JSON.parse(filterString);

    const [data,setData] = useState<any>(null);
    const [checkboxes, setCheckboxes] = useState<CheckboxData[]>([]);
    const [searchText, setSearchText] = useState<string>("");
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const initialSelected = 0;
    const numSelected: number = checkboxes.reduce((accumulator, checkbox) => accumulator + (checkbox.checked ? 1 : 0), initialSelected);

    const filterConfigMap = new Map([
        [componentAttributeTypes.membershipFilter, { labelPlural: "Memberships", labelSingle: "Membership" }],
        [componentAttributeTypes.packageFilter, { labelPlural: "Packages", labelSingle: "Package" }],
        [componentAttributeTypes.staffFilter, { labelPlural: "Staff", labelSingle: "Staff" }]
    ]);

    const config: FilterConfiguration = filterConfigMap.get(definition.attKey) || { labelPlural: "Staff", labelSingle: "Staff" };

    useEffect(() => {
        // When this component mounts, GET all business memberships | packages | staff and store that data into this component's state.
        // This state variable should also hold the checkbox state for each membership, so let's map the data into a Checkbox object that holds data and also a field that holds it's checkbox checked/unchecked boolean state

        const onComplete = (data: any) => {
            let listOfCheckboxData: CheckboxData[] = [];
            if (definition.attKey === componentAttributeTypes.packageFilter) {
                // do package filter
                const packageList = data[0].packageList ?? [];
                listOfCheckboxData = mapPackageListToCheckboxData(packageList, parsedFilter);
            } else if (definition.attKey === componentAttributeTypes.membershipFilter) {
                // membership filter
                const membershipList = data[0].membershipList ?? [];
                listOfCheckboxData = mapMembershipListToCheckboxData(membershipList, parsedFilter); // this should filter the checkboxes based on the componentAttributes
            } else if (definition.attKey === componentAttributeTypes.staffFilter) {
                console.log('debugger; onComplete data: ', data);
                const staffList = data; // list of staff member objects
                listOfCheckboxData = mapStaffListToCheckboxData(staffList, parsedFilter);
            }
            setData(data);
            setCheckboxes(listOfCheckboxData);
        }

        const onFailure = (e: any) => {
            console.error('failed getting business membership', e);
        }

        if (definition.attKey === componentAttributeTypes.packageFilter) {
            // GET packages and filter
            GetBusinessPackages(siteData.userContext, onComplete, onFailure);   
        } else if (definition.attKey === componentAttributeTypes.membershipFilter) {
            // GET memberships and filter
            GetBusinessMemberships(siteData.userContext, onComplete, onFailure);
        } else if (definition.attKey === componentAttributeTypes.staffFilter) {
            // GET staff and filter
            GetBusinessStaff(siteData.userContext, onComplete, onFailure);
        }
    }, []);

    const mapStaffListToCheckboxData = (staffList: any, filterData: FilterData, ) => {
        const { status, excludedIds, includedIds } = filterData;

        const checkboxData: CheckboxData[] = staffList.map((s: any) => {
            // NOTE: concatenation of a staff's first and last name is not a good key as its not unique, or they could change their name. For now don't use this filter until we get a unique ID for each staff member
            const staffFirstAndLastName = `${s.firstName} ${s.lastName}`; 
            const isNewStaff = !includedIds.includes(staffFirstAndLastName) && !excludedIds.includes(staffFirstAndLastName);
            const isChecked = isNewStaff ? (status === FilterDataSelectStatus.All) : (excludedIds.includes(staffFirstAndLastName) === false && includedIds.includes(staffFirstAndLastName));
            return {
                name: staffFirstAndLastName,
                checked: isChecked,
                hashItemId: staffFirstAndLastName,
            }
        });
        return checkboxData;
    };

    const mapPackageListToCheckboxData = (packageList: any, filterData: FilterData) => {
        const { status, excludedIds, includedIds } = filterData;

        const checkboxData: CheckboxData[] = packageList.map((p: any) => {
            const isNewPackage = !includedIds.includes(p.hashPackageId) && !excludedIds.includes(p.hashPackageId);
            const isChecked = isNewPackage ? (status === FilterDataSelectStatus.All) : (excludedIds.includes(p.hashPackageId) === false && includedIds.includes(p.hashPackageId));
            return {
                name: p.packageName,
                checked: isChecked,
                hashItemId: p.hashPackageId,
            }
        });
        return checkboxData;
    };

    const mapMembershipListToCheckboxData = (membershipList: any, filterData: FilterData): CheckboxData[] => {
        const { status, excludedIds, includedIds } = filterData;
        const checkboxData: CheckboxData[] = membershipList.map((m: any) => {
            const isNewData = excludedIds.includes(m.hashMembershipId) === false && includedIds.includes(m.hashMembershipId) === false; // if the membership hashMembershipId is not present in our filter IDs, then it's new data
            const isChecked = isNewData ? (status === FilterDataSelectStatus.All) : (excludedIds.includes(m.hashMembershipId) === false && includedIds.includes(m.hashMembershipId)); 
            return { 
                name: m.membershipName,
                checked: isChecked,
                hashItemId: m.hashMembershipId
            }
        }); 
        return checkboxData;
    };

    let total = checkboxes.length;

    let isAll = numSelected >= total;

    let numText = isAll ? "All" : numSelected.toString();

    return (
        <div className={"wb-input-group"}>

            <div className="flex-row justify-between cursor-pointer" style={{ fontSize: "14px", padding: "4px 0px" }} onClick={() => {


                if (!isExpanded) {
                    setTimeout(() => {
                        let el = document.getElementById("category-filter-input");
                        if (el) {
                            el.focus();
                        }
                    }, 10)
                }
                setIsExpanded(!isExpanded)



            }}>
                <div>{config.labelPlural}</div>
                <span className="flex-row gap-med" style={{ paddingRight: "4px" }}>
                    <span>{(data != null ? numText : "...")} {numSelected == 1 && total > 1 ? config.labelSingle : config.labelPlural} </span>
                    <div style={{ fontSize: "10px", transform: (!isExpanded ? "rotateZ(-90deg)" : ""), transition: ".2s" }}>
                        <DownCaret />
                    </div>

                </span>
            </div>
            {isExpanded &&
                <div>
                    {
                        data != null ?
                            <>
                                <div style={{ margin: "16px 0px 0px 0px" }} >
                                    <div style={{ background: "white", borderRadius: "4px", boxShadow: "0 0 10px 10px #00000005" }}>
                                        <div className="flex-column gap-med w-full " style={{ background: "#F8F8F8", padding: "8px", fontSize: "15px" }}>

                                            <div className="flex-row justify-between w-full" >
                                                <button
                                                    className="invisible-button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        // update UI of checkboxes to be all checked
                                                        setCheckboxes((prevCheckboxes) => {
                                                            return prevCheckboxes.map((checkbox) => {
                                                                return {
                                                                    ...checkbox,
                                                                    checked: true,
                                                                }
                                                            })
                                                        });
                                                        // update filter field in componentAttributes
                                                        const newFilterData: FilterData = {
                                                            status: FilterDataSelectStatus.All,
                                                            includedIds: checkboxes.map(checkbox => checkbox.hashItemId),
                                                            excludedIds: [],
                                                        }
                                                        
                                                        updateFunc(definition.attKey,JSON.stringify(newFilterData),AttributeTargetType.Component);
                                                    }}
                                                >
                                                    Select All
                                                </button>
                                                <button
                                                    className="invisible-button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setCheckboxes((prevCheckboxes) => {
                                                            return prevCheckboxes.map((checkbox) => {
                                                                return {
                                                                    ...checkbox,
                                                                    checked: false,
                                                                }
                                                            })
                                                        });
                                                        const newFilterData: FilterData = {
                                                            status: FilterDataSelectStatus.None,
                                                            includedIds: [],
                                                            excludedIds: checkboxes.map(checkbox => checkbox.hashItemId),
                                                        }
                                                       
                                                        updateFunc(definition.attKey,JSON.stringify(newFilterData),AttributeTargetType.Component);
                                                    }}
                                                >
                                                    Select None
                                                </button>
                                            </div>

                                        </div>
                                        <div className="relative" style={{ padding: "8px" }}>
                                            <input
                                                id="category-filter-input"
                                                style={{ paddingLeft: "30px" }}
                                                className="w-full "
                                                placeholder="Search"
                                                type="text"
                                                value={searchText}
                                                onChange={(e) => {
                                                    // only show checkboxes that include the search text
                                                    setSearchText(e.target.value);
                                                }}
                                            />
                                            <SearchIcon />
                                        </div>
                                        <div className="flex-column" style={{ fontWeight: "normal", padding: "8px 14px" }}>

                                            {/* render a checkbox for each package | membership | staff */}
                                            {checkboxes
                                                .filter((checkbox: CheckboxData) => checkbox.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()))
                                                .map((checkbox: CheckboxData) => (
                                                    <CheckItem
                                                        key={`membership-${checkbox.hashItemId}-checkbox-container`}
                                                        checked={checkbox.checked}
                                                        item={checkbox.name}
                                                        onClickFunc={() => {
                                                            // this function should update the componentAttributes field in LayoutNode - which then gets passed to the /savecontent api

                                                            // Let's create a filter object which we'll store in the component attributes
                                                            // step 1: get a list of checked and unchecked IDs - call it excludedIds and includedIds
                                                            // step 2: update excludedIds and includedIds to reflect the ID of the checkbox clicked (we should add or remove depending on its previous state)
                                                            // step 3: at this point our excludedIds and includedIds should be up to date, so now we can create a updated filter data object and call `updateFunc`(to update LayoutNode) as well as `setCheckboxes` to update the UI

                                                            // step 1: make a list of all checkbox IDs that are false
                                                            let excludedIds = checkboxes
                                                                .filter((checkbox: CheckboxData) => !checkbox.checked)
                                                                .map((checkbox: CheckboxData) => checkbox.hashItemId);

                                                            let includedIds = checkboxes
                                                                .filter((checkbox: CheckboxData) => checkbox.checked)
                                                                .map((checkbox: CheckboxData) => checkbox.hashItemId);

                                                            // step 2: update list of IDs to either remove or add current checkbox ID
                                                            const newCheckedStateOfThisCheckbox = !checkbox.checked; // this updates (ie. flips) the checked state on click
                                                            if (newCheckedStateOfThisCheckbox === false) {
                                                                // add it to our excludedIds
                                                                excludedIds.push(checkbox.hashItemId);
                                                                // remove from includedIds
                                                                includedIds = includedIds.filter(id => id !== checkbox.hashItemId);
                                                            } else {
                                                                // this checkbox is checked so remove from excludedIds
                                                                excludedIds = excludedIds.filter((excludedId) => excludedId !== checkbox.hashItemId);
                                                                // add to includedIds
                                                                includedIds.push(checkbox.hashItemId);
                                                            }

                                                            const status = includedIds.length === checkboxes.length ? FilterDataSelectStatus.All : excludedIds.length === checkboxes.length ? FilterDataSelectStatus.None : FilterDataSelectStatus.Some;

                                                            const filterData: FilterData = {
                                                                status, 
                                                                excludedIds,
                                                                includedIds
                                                            };

                                                            // step 3: update (componentAttributes in LayoutNode) and checkboxes state
                                                            // do we need to change this to componentAttributeTypes.membershipFilter for packages??

                                                            updateFunc(definition.attKey,JSON.stringify(filterData),AttributeTargetType.Component);

                                                            setCheckboxes((prevCheckboxes) => {
                                                                return prevCheckboxes.map((checkbox) => {
                                                                    return {
                                                                        ...checkbox,
                                                                        checked: !excludedIds.includes(checkbox.hashItemId) && includedIds.includes(checkbox.hashItemId), // consider this checkbox checked if it's ID is not excludedIds AND is in includedIds
                                                                    }
                                                                });
                                                            });

                                                        }}
                                                    />
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                            :
                            <div className="w-full " style={{ opacity: ".75", padding: "24px 0px", fontSize: "14px" }}>Loading...</div>

                    }
                </div>
            }
        </div>
    );
};

export default MembershipFilterField;
